/**
 * QuizGradingV2 - Dedicated Split-Screen Grading Workspace for Quiz Activities
 *
 * Implements the full-screen split-screen workspace mirroring ProgrammingActivityV2 / GradingV2:
 * - Left Pane: Question prompt, student submitted response (formatted text or embedded file viewer)
 * - Right Pane: Interactive rubric evaluation matrix (RubricScoreGrid) or manual fallback
 * - Top Bar: Student navigation (< Prev, Searchable Dropdown, Next >), Subjective Question switcher tabs, Graded status badge
 * - Shortcuts: Ctrl+Enter to Save Grade and automatically advance to next student
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Breadcrumb, Spinner, OverlayTrigger, Tooltip, Dropdown } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import SplitPane from 'react-split-pane';
import parse from 'html-react-parser';
import {
  FiChevronLeft,
  FiChevronRight,
  FiArrowRight,
  FiArrowLeft,
  FiFileText,
  FiDownload,
  FiCheckCircle,
  FiAlertCircle,
  FiChevronDown,
  FiChevronUp,
  FiSearch,
  FiX,
  FiCheck,
  FiInfo,
  FiRotateCcw,
} from 'react-icons/fi';
import * as constants from 'Constants';
import httpService from 'services/httpService';
import displayGlobalMessage from 'Utils/DisplayGlobalMessage';
import log from 'loglevel';
import DocumentView from 'Screens1/Student/Course/Content/Multimedia/Components/DocumentView/DocumentView';
import { RubricScoreGrid } from 'Components/Common/Rubrics';
import '../../../Programming/Components/SubjectiveGrading/splitter.scss';
import styles from './QuizGradingV2.module.css';

const toRelativeFilePath = (rawUrl) => {
  if (!rawUrl) return '';
  try {
    let path = rawUrl;
    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
      const parsed = new URL(rawUrl);
      path = `${parsed.pathname}${parsed.search}`;
    }
    if (path.startsWith('/api/media/')) {
      path = path.replace('/api/media/', '/media/');
    }
    return path;
  } catch (err) {
    return rawUrl;
  }
};

// Stable DocViewer config reference - preventing document reload on keystrokes
const QUIZ_DOC_VIEWER_CONFIG = {
  header: {},
  pdfZoom: {
    defaultZoom: typeof window !== 'undefined' && window.innerWidth < 768 ? 1.2 : 0.8,
    zoomJump: 0.2,
  },
  pdfVerticalScrollByDefault: true,
};

// Clean HTML text helper
const cleanQuestionText = (text) => {
  if (!text) return '';
  let cleaned = text;
  const remarksIndex = cleaned.indexOf('REMARKS=');
  if (remarksIndex !== -1) {
    cleaned = cleaned.slice(0, remarksIndex);
  }
  cleaned = cleaned.replace(/style=".*?"/g, '');
  cleaned = cleaned.replace(/font\s+size\s*=\s*"\d+"/g, 'font size="16px"');
  cleaned = cleaned.replace(/<!--.*?-->/gs, '');
  return cleaned;
};

// Helper to determine question type label
const getQuestionTypeLabel = (type) => {
  switch (type) {
    case 'D':
      return 'Descriptive';
    case 'T':
      return 'Short Answer';
    case 'U':
      return 'File Upload';
    case 'PA':
      return 'Programming';
    default:
      return 'Subjective';
  }
};

const QuizGradingV2 = () => {
  const { courseId, labId, activityID, studentID, questionId: paramQuestionId } = useParams();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.userInfo);
  const isStudent = userInfo?.mode === 'S';

  const decodedStudentID = useMemo(() => {
    try {
      return decodeURIComponent(studentID || '');
    } catch {
      return studentID || '';
    }
  }, [studentID]);

  const effectiveStudentID = useMemo(() => {
    if (isStudent) {
      return userInfo?.username || userInfo?.email || decodedStudentID || '';
    }
    return decodedStudentID;
  }, [isStudent, userInfo, decodedStudentID]);

  // Activity & Lab state
  const [labTitle, setLabTitle] = useState('Lab');
  const [activityData, setActivityData] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(true);

  // Students list state
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Student submission state
  const [studentSubmission, setStudentSubmission] = useState(null);
  const [loadingSubmission, setLoadingSubmission] = useState(false);

  // Selected subjective question state
  const [activeQuestionId, setActiveQuestionId] = useState(
    paramQuestionId ? parseInt(paramQuestionId, 10) : null
  );

  // Rubric data for active question
  const [rubricData, setRubricData] = useState(null);
  const [loadingRubric, setLoadingRubric] = useState(false);

  // Manual fallback grading state
  const [manualMarks, setManualMarks] = useState('');
  const [manualRemarks, setManualRemarks] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Solution toggle state
  const [showSolution, setShowSolution] = useState(false);

  // File submission text preview state
  const [submissionTextContent, setSubmissionTextContent] = useState('');
  const [loadingTextContent, setLoadingTextContent] = useState(false);

  // Fetch Lab Details
  useEffect(() => {
    const fetchLab = async () => {
      if (!labId) return;
      try {
        const url = `${constants.base_url}/lab/all/${labId}?courseId=${courseId}`;
        const res = await httpService.get(url);
        if (res.data?.lab?.title) {
          setLabTitle(res.data.lab.title);
        }
      } catch (err) {
        log.error('Error fetching lab details:', err);
      }
    };
    fetchLab();
  }, [labId, courseId]);

  // Fetch Activity Details
  useEffect(() => {
    const fetchActivity = async () => {
      if (!activityID) return;
      setLoadingActivity(true);
      try {
        const url = `${constants.base_url}/lab/activity/${activityID}?type=other`;
        const res = await httpService.get(url);
        const act = res.data?.other_activity;
        if (act) {
          setActivityData(act);
        }
      } catch (err) {
        log.error('Error fetching activity details:', err);
        displayGlobalMessage('Error loading quiz activity details', 'error');
      } finally {
        setLoadingActivity(false);
      }
    };
    fetchActivity();
  }, [activityID]);

  // Subjective questions list
  const subjectiveQuestions = useMemo(() => {
    const questions = activityData?.question_set || [];
    return questions.filter((q) =>
      ['D', 'T', 'U', 'PA'].includes(q.question_type)
    );
  }, [activityData]);

  // Ensure activeQuestionId is set once questions are loaded
  useEffect(() => {
    if (subjectiveQuestions.length > 0) {
      if (!activeQuestionId || !subjectiveQuestions.some((q) => q.id === activeQuestionId)) {
        setActiveQuestionId(subjectiveQuestions[0].id);
      }
    }
  }, [subjectiveQuestions, activeQuestionId]);

  // Sync activeQuestionId with paramQuestionId if it changes
  useEffect(() => {
    if (paramQuestionId) {
      setActiveQuestionId(parseInt(paramQuestionId, 10));
    }
  }, [paramQuestionId]);

  // Fetch All Students List
  useEffect(() => {
    if (isStudent) {
      if (userInfo) {
        setStudents([
          {
            username: userInfo.username,
            email: userInfo.email || userInfo.username,
            first_name: userInfo.name || userInfo.first_name || 'My',
            last_name: userInfo.last_name || 'Submission',
            student_id: userInfo.username,
          },
        ]);
      }
      setLoadingStudents(false);
      return;
    }

    const fetchStudents = async () => {
      if (!activityID) return;
      setLoadingStudents(true);
      try {
        const url = `${constants.base_url}/otherlabs/activity/${activityID}/other_activity_submissions/?page=1&page_size=500`;
        const res = await httpService.get(url);
        setStudents(res.data?.data || []);
      } catch (err) {
        log.error('Error loading students list:', err);
      } finally {
        setLoadingStudents(false);
      }
    };
    fetchStudents();
  }, [activityID, isStudent, userInfo]);

  // Fetch Current Student's Submission
  const fetchSubmission = useCallback(async (identifier) => {
    if (!activityID || !identifier) return;
    setLoadingSubmission(true);
    try {
      let url = `${constants.base_url}/otherlabs/activity/${activityID}/other_activity_submissions/${encodeURIComponent(identifier)}/`;
      let res = await httpService.get(url);
      let data = res.data?.data;
      if (data && data[identifier]) {
        setStudentSubmission(data[identifier]);
      } else if (data && Object.keys(data).length > 0) {
        const firstKey = Object.keys(data)[0];
        setStudentSubmission(data[firstKey]);
      } else if (isStudent && userInfo?.email && userInfo.email !== identifier) {
        url = `${constants.base_url}/otherlabs/activity/${activityID}/other_activity_submissions/${encodeURIComponent(userInfo.email)}/`;
        res = await httpService.get(url);
        data = res.data?.data;
        if (data && (data[userInfo.email] || Object.keys(data).length > 0)) {
          const key = data[userInfo.email] ? userInfo.email : Object.keys(data)[0];
          setStudentSubmission(data[key]);
        } else {
          setStudentSubmission(null);
        }
      } else {
        setStudentSubmission(null);
      }
    } catch (err) {
      log.error('Error fetching student submission:', err);
      setStudentSubmission(null);
    } finally {
      setLoadingSubmission(false);
    }
  }, [activityID, isStudent, userInfo]);

  useEffect(() => {
    if (effectiveStudentID) {
      fetchSubmission(effectiveStudentID);
    }
  }, [effectiveStudentID, fetchSubmission]);

  // Current active question object
  const currentQuestion = useMemo(() => {
    return subjectiveQuestions.find((q) => q.id === activeQuestionId) || subjectiveQuestions[0] || null;
  }, [subjectiveQuestions, activeQuestionId]);

  // Current student response for the active question
  const currentResponse = useMemo(() => {
    if (!studentSubmission?.responses || !currentQuestion) return null;
    return studentSubmission.responses.find((r) => r.question === currentQuestion.id) || null;
  }, [studentSubmission, currentQuestion]);

  // Fetch Rubric for the active question
  useEffect(() => {
    const fetchRubric = async () => {
      if (!currentQuestion?.id) {
        setRubricData(null);
        return;
      }
      setLoadingRubric(true);
      try {
        const url = `${constants.base_url}/otherlabs/question/${currentQuestion.id}/rubrics/`;
        const res = await httpService.get(url);
        if (res.data?.rubric && res.data.rubric.length > 0) {
          setRubricData(res.data.rubric);
        } else {
          setRubricData(null);
        }
      } catch (err) {
        log.error('Error fetching question rubric:', err);
        setRubricData(null);
      } finally {
        setLoadingRubric(false);
      }
    };

    fetchRubric();
  }, [currentQuestion?.id]);

  // Sync manual fallback inputs when currentResponse changes
  useEffect(() => {
    if (currentResponse?.graded) {
      setManualMarks(currentResponse.scored_marks !== undefined ? String(currentResponse.scored_marks) : '');
      setManualRemarks(currentResponse.remark || '');
    } else {
      setManualMarks('');
      setManualRemarks('');
    }
  }, [currentResponse?.id, currentResponse?.graded, currentResponse?.scored_marks, currentResponse?.remark]);

  // Helper to normalize submission file URL (resolving relative /media/ paths)
  const getSubmissionFileUrl = useCallback((resp) => {
    if (!resp) return '';
    const raw = resp.response_file || resp.response_text || resp.file_url;
    if (!raw || typeof raw !== 'string') return '';
    let trimmed = raw.trim();

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed.replace('/api/api/', '/api/');
    }

    const cleanBase = (constants.base_url || '').replace(/\/+$/, '');
    if (trimmed.startsWith('/api/')) {
      const origin = cleanBase.replace(/\/api$/, '');
      return `${origin}${trimmed}`;
    }
    if (trimmed.startsWith('/')) {
      return `${cleanBase}${trimmed}`;
    }
    return `${cleanBase}/${trimmed}`;
  }, []);

  // Fetch text file content when file submission is text or code
  useEffect(() => {
    const fUrl = getSubmissionFileUrl(currentResponse);
    if (!fUrl) {
      setSubmissionTextContent('');
      return;
    }
    const isText = /\.(txt|csv|json|py|c|cpp|java|html|xml|log|md)$/i.test(fUrl);
    if (isText) {
      setLoadingTextContent(true);
      fetch(fUrl)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP error ${res.status}`);
          return res.text();
        })
        .then((text) => setSubmissionTextContent(text))
        .catch((err) => {
          log.error('Error fetching text submission content:', err);
          setSubmissionTextContent('');
        })
        .finally(() => setLoadingTextContent(false));
    } else {
      setSubmissionTextContent('');
    }
  }, [currentResponse, getSubmissionFileUrl]);

  // Memoized sub-section data for DocumentView so it doesn't re-render on grading inputs
  const currentSubmissionSubSection = useMemo(() => {
    if (!currentResponse) return null;
    const fUrl = getSubmissionFileUrl(currentResponse);
    if (!fUrl) return null;
    const fileName =
      currentResponse?.response_file?.split('/').pop() ||
      fUrl.split('/').pop() ||
      'submission-file';
    const relativeFile = toRelativeFilePath(fUrl);
    return {
      data: {
        document_file: relativeFile,
        title: fileName,
        description: '',
      },
    };
  }, [currentResponse, getSubmissionFileUrl]);

  // Validated marks input handler to ensure marks do not exceed question max
  const handleManualMarksChange = (e) => {
    const val = e.target.value;
    if (val === '') {
      setManualMarks('');
      return;
    }
    const maxMarks = currentQuestion?.marks || 0;
    const num = parseFloat(val);
    if (!isNaN(num)) {
      if (num < 0) {
        setManualMarks('0');
        return;
      }
      if (num > maxMarks) {
        displayGlobalMessage(`Marks cannot exceed maximum marks (${maxMarks})`, 'warning');
        setManualMarks(String(maxMarks));
        return;
      }
    }
    setManualMarks(val);
  };

  // Search filter for student dropdown
  const [studentSearch, setStudentSearch] = useState('');
  const [studentDropdownOpen, setStudentDropdownOpen] = useState(false);

  // Student Navigation calculations
  const currentStudentIndex = useMemo(() => {
    return students.findIndex(
      (s) => (s.email || s.username || s.student_id) === decodedStudentID
    );
  }, [students, decodedStudentID]);

  const prevStudent = currentStudentIndex > 0 ? students[currentStudentIndex - 1] : null;
  const nextStudent =
    currentStudentIndex >= 0 && currentStudentIndex < students.length - 1
      ? students[currentStudentIndex + 1]
      : null;

  // Current student object
  const currentStudent = useMemo(() => {
    return (
      students.find(
        (s) => (s.email || s.username || s.student_id) === decodedStudentID
      ) || null
    );
  }, [students, decodedStudentID]);

  // Filtered students for searchable dropdown
  const filteredStudents = useMemo(() => {
    if (!studentSearch.trim()) return students;
    const q = studentSearch.toLowerCase();
    return students.filter((s) => {
      const email = (s.email || '').toLowerCase();
      const first = (s.first_name || '').toLowerCase();
      const last = (s.last_name || '').toLowerCase();
      const studId = (s.student_id || '').toLowerCase();
      const roll = (s.roll_no || '').toLowerCase();
      const username = (s.username || '').toLowerCase();
      return (
        email.includes(q) ||
        first.includes(q) ||
        last.includes(q) ||
        studId.includes(q) ||
        roll.includes(q) ||
        username.includes(q)
      );
    });
  }, [students, studentSearch]);

  // Get actual 1-based question number within the entire quiz question set
  const getActualQuestionNumber = useCallback(
    (qId) => {
      const allQuestions = activityData?.question_set || [];
      const idx = allQuestions.findIndex((q) => q.id === qId);
      return idx !== -1 ? idx + 1 : 1;
    },
    [activityData]
  );

  // Handle switching student
  const handleStudentSelect = (targetEmail) => {
    if (!targetEmail) return;
    setStudentDropdownOpen(false);
    setStudentSearch('');
    const encoded = encodeURIComponent(targetEmail);
    navigate(
      `/courseware/course/${courseId}/content/labs/${labId}/activities/other/${activityID}/submissions/${encoded}/grade/${activeQuestionId || ''}`
    );
  };

  // Handle switching active question
  const handleQuestionSelect = (qId) => {
    setActiveQuestionId(qId);
    const encoded = encodeURIComponent(decodedStudentID);
    if (isStudent) {
      navigate(
        `/courseware/course/${courseId}/content/labs/${labId}/activities/other/${activityID}/SubjectiveGrading/${qId}`,
        { replace: true }
      );
    } else {
      navigate(
        `/courseware/course/${courseId}/content/labs/${labId}/activities/other/${activityID}/submissions/${encoded}/grade/${qId}`,
        { replace: true }
      );
    }
  };

  // Advance to next student
  const handleNextStudent = useCallback(() => {
    if (nextStudent) {
      const targetId = nextStudent.email || nextStudent.username || nextStudent.student_id;
      handleStudentSelect(targetId);
    } else {
      displayGlobalMessage('You have reached the last student.', 'info');
    }
  }, [nextStudent, handleStudentSelect]);

  // Save Rubric Evaluation
  const handleSaveRubricGrade = async ({ ratings, comments, andNext }) => {
    if (!currentQuestion?.id || !decodedStudentID) return;
    setIsSaving(true);
    try {
      const graderName = userInfo?.name || userInfo?.username || 'Instructor';
      const payload = {
        student_id: decodedStudentID,
        question: currentQuestion.id,
        ratings: ratings,
        comments: comments || '',
        graded_by: graderName,
      };

      const url = `${constants.base_url}/otherlabs/activity/${activityID}/grade_submission/`;
      await httpService.post(url, payload);

      displayGlobalMessage('Rubric Grade Saved Successfully', 'success');

      // Refresh student submission data
      await fetchSubmission(decodedStudentID);

      if (andNext && nextStudent) {
        handleNextStudent();
      }
    } catch (err) {
      log.error('Error saving rubric grade:', err);
      displayGlobalMessage(err.response?.data?.error || 'Failed to save grade', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Save Manual Grade (when no rubric)
  const handleSaveManualGrade = async (andNext = false) => {
    if (!currentQuestion?.id || !decodedStudentID) return;
    const numericMarks = parseFloat(manualMarks);
    if (isNaN(numericMarks) || numericMarks < 0 || numericMarks > (currentQuestion.marks || 0)) {
      displayGlobalMessage(`Marks must be between 0 and ${currentQuestion.marks}`, 'error');
      return;
    }

    setIsSaving(true);
    try {
      const graderName = userInfo?.name || userInfo?.username || 'Instructor';
      const payload = {
        student_id: decodedStudentID,
        question: currentQuestion.id,
        scored_marks: numericMarks,
        remark: manualRemarks || '',
        graded_by: graderName,
      };

      const url = `${constants.base_url}/otherlabs/activity/${activityID}/grade_submission/`;
      await httpService.post(url, payload);

      displayGlobalMessage('Grade Saved Successfully', 'success');

      // Refresh student submission data
      await fetchSubmission(decodedStudentID);

      if (andNext && nextStudent) {
        handleNextStudent();
      }
    } catch (err) {
      log.error('Error saving manual grade:', err);
      displayGlobalMessage(err.response?.data?.error || 'Failed to save grade', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // File download helper
  const handleDownloadFile = useCallback(async () => {
    const directFileUrl = getSubmissionFileUrl(currentResponse);
    if (directFileUrl) {
      const fileName =
        currentResponse?.response_file?.split('/').pop() ||
        currentResponse?.response_text?.split('/').pop() ||
        directFileUrl.split('/').pop() ||
        `submission_${currentQuestion?.id || 'file'}`;
      try {
        const response = await fetch(directFileUrl);
        if (!response.ok) throw new Error('Network error');
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        return;
      } catch (err) {
        log.warn('Direct fetch failed, falling back to window.open or archive:', err);
        window.open(directFileUrl, '_blank', 'noopener,noreferrer');
        return;
      }
    }

    try {
      const url = `${constants.base_url}/otherlabs/activity/${activityID}/other_submission_download_lab_files/?stud_id=${encodeURIComponent(decodedStudentID)}&username=${encodeURIComponent(decodedStudentID)}`;
      const response = await httpService.get(url, { responseType: 'blob' });
      const filename = `${decodedStudentID}_lab_${activityID}_files.tar.gz`;
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      log.error('Error downloading file:', error);
      displayGlobalMessage('Failed to download student file', 'error');
    }
  }, [currentResponse, getSubmissionFileUrl, currentQuestion?.id, activityID, decodedStudentID]);

  // Global Ctrl+Enter shortcut for Save & Next in manual grading mode
  useEffect(() => {
    if (isStudent || (rubricData && rubricData.length > 0)) return;

    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault();
        handleSaveManualGrade(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isStudent, rubricData, manualMarks, manualRemarks, currentQuestion, decodedStudentID, nextStudent]);

  // Loading state (placed strictly after all hooks)
  if (loadingActivity) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinnerContainer}>
          <Spinner animation="border" variant="primary" />
          <span>Loading Quiz Grading Workspace...</span>
        </div>
      </div>
    );
  }

  const isGraded = Boolean(currentResponse?.graded);

  return (
    <div className={styles.container}>
      {/* Breadcrumb Navigation */}
      <div className={styles.breadcrumbContainer}>
        <Breadcrumb className="mb-0">
          <Breadcrumb.Item href={`/courseware/course/${courseId}/content/labs`}>
            Labs
          </Breadcrumb.Item>
          <Breadcrumb.Item href={`/courseware/course/${courseId}/content/labs/${labId}/`}>
            {labTitle}
          </Breadcrumb.Item>
          {isStudent ? (
            <>
              <Breadcrumb.Item
                href={`/courseware/course/${courseId}/content/labs/${labId}/other/${activityID}`}
              >
                {activityData?.name || activityData?.title || 'Quiz'}
              </Breadcrumb.Item>
              <Breadcrumb.Item active>Subjective Grading</Breadcrumb.Item>
            </>
          ) : (
            <>
              <Breadcrumb.Item
                href={`/courseware/course/${courseId}/content/labs/${labId}/activities/other/${activityID}/submissions`}
              >
                {activityData?.name || activityData?.title || 'Quiz'} Submissions
              </Breadcrumb.Item>
              <Breadcrumb.Item
                href={`/courseware/course/${courseId}/content/labs/${labId}/activities/other/${activityID}/submissions/${encodeURIComponent(decodedStudentID)}`}
              >
                {decodedStudentID}
              </Breadcrumb.Item>
              <Breadcrumb.Item active>Subjective Grading</Breadcrumb.Item>
            </>
          )}
        </Breadcrumb>
      </div>

      {/* Modern Top Header / Navigation Bar */}
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => {
              if (isStudent) {
                navigate(`/courseware/course/${courseId}/content/labs/${labId}/other/${activityID}`);
              } else {
                navigate(
                  `/courseware/course/${courseId}/content/labs/${labId}/activities/other/${activityID}/submissions/${encodeURIComponent(decodedStudentID)}`
                );
              }
            }}
            title={isStudent ? 'Back to Quiz Activity' : 'Back to Quiz Submissions'}
          >
            <FiArrowLeft size={16} />
            <span>{isStudent ? 'Back to Quiz' : 'Back to Submissions'}</span>
          </button>

          {/* Student Selector / Current Student Display */}
          {!isStudent ? (
            <div className={styles.studentNavGroup}>
              {/* Prev Student */}
              <OverlayTrigger overlay={<Tooltip>Previous Student</Tooltip>}>
                <button
                  type="button"
                  className={styles.navBtn}
                  disabled={!prevStudent}
                  onClick={() => {
                    if (!prevStudent) return;
                    const studentId =
                      prevStudent.email ||
                      prevStudent.username ||
                      prevStudent.student_id;
                    if (studentId) {
                      handleStudentSelect(studentId);
                    }
                  }}
                >
                  <FiChevronLeft size={18} />
                </button>
              </OverlayTrigger>

              {/* Searchable Student Dropdown */}
              <Dropdown
                show={studentDropdownOpen}
                onToggle={(isOpen) => setStudentDropdownOpen(isOpen)}
                className={styles.studentDropdownWrapper}
              >
                <Dropdown.Toggle
                  as="button"
                  type="button"
                  className={styles.studentDropdownToggle}
                  id="student-search-dropdown"
                >
                  <div className={styles.studentDropdownLabel}>
                    <span className={styles.studentName}>
                      {currentStudent
                        ? `${currentStudent.first_name || ''} ${currentStudent.last_name || ''}`.trim() ||
                          currentStudent.email ||
                          currentStudent.username ||
                          currentStudent.student_id
                        : decodedStudentID}
                    </span>
                    <span className={styles.studentEmail}>
                      {currentStudent?.email ||
                        currentStudent?.username ||
                        decodedStudentID}
                    </span>
                  </div>
                </Dropdown.Toggle>

                <Dropdown.Menu className={styles.studentDropdownMenu}>
                  {/* Search Input */}
                  <div
                    className={styles.studentSearchInputWrapper}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FiSearch size={14} className={styles.searchIcon} />
                    <input
                      type="text"
                      className={styles.studentSearchInput}
                      placeholder="Search by name, email, roll number..."
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                    {studentSearch && (
                      <button
                        type="button"
                        className={styles.clearSearchBtn}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setStudentSearch('');
                        }}
                      >
                        <FiX size={14} />
                      </button>
                    )}
                  </div>

                  {/* Student List */}
                  <div className={styles.studentListScroll}>
                    {filteredStudents.length > 0 ? (
                      filteredStudents.map((stud) => {
                        const studentId =
                          stud.email ||
                          stud.username ||
                          stud.student_id;
                        const fullName = `${stud.first_name || ''} ${stud.last_name || ''}`.trim();
                        const isSelected = studentId === decodedStudentID;

                        return (
                          <Dropdown.Item
                            key={studentId}
                            active={isSelected}
                            className={`${styles.studentDropdownItem} ${
                              isSelected ? styles.studentItemActive : ''
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!studentId) return;
                              handleStudentSelect(studentId);
                              setStudentDropdownOpen(false);
                              setStudentSearch('');
                            }}
                          >
                            <div className={styles.studentItemInfo}>
                              <div className={styles.studentItemName}>
                                {fullName || studentId}
                              </div>
                              <div className={styles.studentItemSub}>
                                {stud.email || stud.username || ''}
                                {stud.roll_no ? ` • ${stud.roll_no}` : ''}
                              </div>
                            </div>

                            {isSelected && (
                              <FiCheck size={16} className="text-primary ms-auto" />
                            )}
                          </Dropdown.Item>
                        );
                      })
                    ) : (
                      <div className={styles.noStudentsFound}>
                        {studentSearch
                          ? `No students match "${studentSearch}"`
                          : 'No students found'}
                      </div>
                    )}
                  </div>
                </Dropdown.Menu>
              </Dropdown>

              {/* Next Student */}
              <OverlayTrigger overlay={<Tooltip>Next Student</Tooltip>}>
                <button
                  type="button"
                  className={styles.navBtn}
                  disabled={!nextStudent}
                  onClick={() => {
                    if (nextStudent) {
                      handleNextStudent();
                    }
                  }}
                >
                  <FiChevronRight size={18} />
                </button>
              </OverlayTrigger>
            </div>
          ) : (
            <div className={styles.studentInfoBadge}>
              <span className={styles.studentName}>
                {userInfo?.name || userInfo?.username || 'My Submission'}
              </span>
            </div>
          )}

          {/* Top Left Status Badge */}
          <div className={styles.statusBadgeContainer}>
            <span
              className={`${styles.statusBadge} ${
                isGraded ? styles.statusGraded : styles.statusUngraded
              }`}
            >
              {isGraded ? (
                <>
                  <FiCheckCircle size={14} className="me-1" />
                  Graded ({currentResponse?.scored_marks ?? 0} / {currentQuestion?.marks ?? 0} pts)
                </>
              ) : (
                <>
                  <FiAlertCircle size={14} className="me-1" />
                  Ungraded
                </>
              )}
            </span>
          </div>
        </div>

        <div className={styles.topBarRight}>
          {!isStudent && (
            <span className={styles.studentCounterText}>
              Student {currentStudentIndex >= 0 ? currentStudentIndex + 1 : 1} of {students.length}
            </span>
          )}
        </div>
      </div>

      {/* Question Selector Tabs */}
      {subjectiveQuestions.length > 0 && (
        <div className={styles.questionTabsBar}>
          <span className={styles.questionTabsLabel}>Subjective Questions:</span>
          {subjectiveQuestions.map((q) => {
            const resp = studentSubmission?.responses?.find((r) => r.question === q.id);
            const graded = resp?.graded;
            const isActive = q.id === currentQuestion?.id;
            const actualNum = getActualQuestionNumber(q.id);

            return (
              <button
                key={q.id}
                type="button"
                className={`${styles.questionTab} ${isActive ? styles.questionTabActive : ''}`}
                onClick={() => handleQuestionSelect(q.id)}
              >
                <span
                  className={graded ? styles.gradedIndicator : styles.ungradedIndicator}
                  title={graded ? 'Graded' : 'Ungraded'}
                />
                <span className={styles.questionTabTitle}>
                  Q{actualNum} ({getQuestionTypeLabel(q.question_type)})
                </span>
                <span className={styles.questionTabMarks}>
                  {q.marks} pts
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Split Pane Grading Workspace */}
      <div className={styles.splitWrapper}>
        <SplitPane split="vertical" defaultSize="50%" minSize={380} maxSize={-380}>
          {/* Left Pane: Question Prompt & Student Response */}
          <div className={styles.leftPane}>
            {currentQuestion ? (
              <>
                {/* Question Prompt Card */}
                <div className={styles.sectionCard}>
                  <div className={styles.sectionHeader}>
                    <h4 className={styles.sectionTitle}>
                      Question {getActualQuestionNumber(currentQuestion.id)}
                    </h4>
                    <div className={styles.questionMeta}>
                      <span className={`${styles.badge} ${styles.badgeType}`}>
                        {getQuestionTypeLabel(currentQuestion.question_type)}
                      </span>
                      <span className={`${styles.badge} ${styles.badgeMarks}`}>
                        {currentQuestion.marks} Marks
                      </span>
                    </div>
                  </div>
                  <div className={styles.questionTextContent}>
                    {parse(cleanQuestionText(currentQuestion.statement || currentQuestion.question_text || ''))}
                  </div>

                  {/* Optional Solution Card */}
                  {Boolean(
                    (currentQuestion.solution || currentQuestion.answer_explaination) &&
                    (!isStudent || activityData?.reveal_answers)
                  ) && (
                    <div className={styles.solutionCard}>
                      <div
                        className={styles.solutionHeader}
                        onClick={() => setShowSolution((prev) => !prev)}
                      >
                        <span>Expected Solution / Answer Key</span>
                        {showSolution ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                      </div>
                      {showSolution && (
                        <div className={styles.solutionBody}>
                          {parse(cleanQuestionText(currentQuestion.solution || currentQuestion.answer_explaination || ''))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Student Response Card */}
                <div className={styles.sectionCard}>
                  <div className={styles.sectionHeader}>
                    <h4 className={styles.sectionTitle}>
                      {isStudent ? 'Your Submission' : "Student's Submission"}
                    </h4>
                    {(currentResponse?.created_at || currentResponse?.submitted_on) && (
                      <span className="small text-muted">
                        Submitted: {new Date(currentResponse.created_at || currentResponse.submitted_on).toLocaleString()}
                      </span>
                    )}
                  </div>

                  {loadingSubmission ? (
                    <div className="text-center py-4 text-muted">
                      <Spinner animation="border" size="sm" />
                      <div className="mt-2 small">Loading submission...</div>
                    </div>
                  ) : currentResponse ? (
                    currentQuestion.question_type === 'U' ? (
                      /* File Upload Question Response */
                      <div className={styles.fileAttachmentBox}>
                        {(() => {
                          const fUrl = getSubmissionFileUrl(currentResponse);
                          if (!fUrl) {
                            return (
                              <div className={styles.emptySubmission}>
                                No file uploaded for this question.
                              </div>
                            );
                          }

                          const fileName =
                            currentResponse?.response_file?.split('/').pop() ||
                            currentResponse?.response_text?.split('/').pop() ||
                            fUrl.split('/').pop() ||
                            'Uploaded Submission Document';

                          const isImg = /\.(png|jpe?g|gif|webp|svg)$/i.test(fUrl);
                          const isText = /\.(txt|csv|json|py|c|cpp|java|html|xml|log|md)$/i.test(fUrl);

                          return (
                            <>
                              <div className={styles.fileInfoBar}>
                                <div className="d-flex align-items-center gap-2">
                                  <FiFileText size={20} className="text-primary" />
                                  <span className="fw-medium text-truncate" style={{ maxWidth: '400px' }}>
                                    {fileName}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                                  onClick={handleDownloadFile}
                                >
                                  <FiDownload size={14} /> Download File
                                </button>
                              </div>

                              {/* File Embedded Viewer if Image, Text, or Document */}
                              {isImg ? (
                                <div className="text-center p-2 bg-light rounded">
                                  <img src={fUrl} alt="Student upload" className={styles.imagePreview} />
                                </div>
                              ) : isText ? (
                                <div className={styles.textPreviewContainer}>
                                  {loadingTextContent ? (
                                    <div className="text-center py-3 text-muted">
                                      <Spinner animation="border" size="sm" />
                                      <span className="ms-2 small">Loading file preview...</span>
                                    </div>
                                  ) : (
                                    <pre className={styles.textPreviewBox}>
                                      {submissionTextContent || '(Empty file)'}
                                    </pre>
                                  )}
                                </div>
                              ) : currentSubmissionSubSection ? (
                                <div className={styles.documentViewerWrapper}>
                                  <DocumentView
                                    hideHeader={true}
                                    selectedSubSection={currentSubmissionSubSection}
                                    viewerConfig={QUIZ_DOC_VIEWER_CONFIG}
                                  />
                                </div>
                              ) : (
                                <div className={styles.unsupportedPreviewCard}>
                                  <div className="text-muted small mb-2">
                                    Preview is not available for this file format ({fUrl.split('.').pop()}).
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1"
                                    onClick={handleDownloadFile}
                                  >
                                    <FiDownload size={14} /> Download to View
                                  </button>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      /* Text / Descriptive Question Response */
                      <div className={styles.submissionAnswerBox}>
                        {currentResponse.response_text ? (
                          currentResponse.response_text
                        ) : (
                          <div className={styles.emptySubmission}>
                            No written response submitted.
                          </div>
                        )}
                      </div>
                    )
                  ) : (
                    <div className={styles.emptySubmission}>
                      No submission record found for this student.
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className={styles.emptySubmission}>
                No subjective questions found in this quiz activity.
              </div>
            )}
          </div>

          {/* Right Pane: Rubric Evaluation or Manual Grading */}
          <div className={styles.rightPane}>
            {loadingRubric ? (
              <div className={styles.loadingSpinnerContainer}>
                <Spinner animation="border" variant="primary" size="sm" />
                <span>Loading Rubric Matrix...</span>
              </div>
            ) : rubricData && rubricData.length > 0 ? (
              /* Rubric-Based Grading Workspace */
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className={styles.sectionTitle} style={{ margin: 0 }}>
                    Rubric Evaluation Matrix
                  </h4>
                  {!isStudent && <span className="small text-muted">Ctrl + Enter to Save & Next</span>}
                </div>

                <RubricScoreGrid
                  rubric={rubricData}
                  initialEvaluation={currentResponse?.rubric_evaluation || []}
                  initialRemark={currentResponse?.remark || ''}
                  maxQuestionMarks={currentQuestion?.marks || 0}
                  onSaveGrade={handleSaveRubricGrade}
                  onNextStudent={handleNextStudent}
                  hasNextStudent={Boolean(nextStudent)}
                  isSaving={isSaving}
                  isStudentView={isStudent}
                  readOnly={isStudent}
                />
              </div>
            ) : isStudent ? (
              /* Student Direct Scoring View (Read-Only) */
              <div className={styles.manualGradingCard}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className={styles.sectionTitle} style={{ margin: 0 }}>
                    Evaluation Results
                  </h4>
                </div>

                <div className={styles.studentScoreSummaryCard}>
                  <div className={styles.scoreRow}>
                    <span className={styles.scoreLabel}>Awarded Marks:</span>
                    <span className={styles.scoreValue}>
                      <b style={{ color: '#059669', fontSize: '18px' }}>
                        {currentResponse?.scored_marks ?? 0}
                      </b>
                      {' '}/ {currentQuestion?.marks || 0} pts
                    </span>
                  </div>

                  <div className={styles.evaluatorRow}>
                    <span className={styles.evaluatorLabel}>Graded By:</span>
                    <span className={styles.evaluatorValue}>
                      {currentResponse?.graded_by && currentResponse.graded_by !== 'NA' && currentResponse.graded_by !== 'None'
                        ? currentResponse.graded_by
                        : (isGraded ? 'Instructor' : 'Not Graded Yet')}
                    </span>
                  </div>

                  {currentResponse?.remark && (
                    <div className={styles.remarksBlock}>
                      <span className={styles.remarksLabel}>Evaluator Feedback / Remarks:</span>
                      <div className={styles.remarksText}>
                        {currentResponse.remark}
                      </div>
                    </div>
                  )}

                  {!isGraded && (
                    <div className="alert alert-warning mt-3 mb-0 py-2 px-3 small">
                      Grading is currently pending for this question.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Direct Scoring Fallback for Instructor (when no rubric is configured) */
              <div className={styles.manualGradingCard}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className={styles.sectionTitle} style={{ margin: 0 }}>
                    Direct Evaluation
                  </h4>
                  <span className="small text-muted">Ctrl + Enter to Save & Next</span>
                </div>

                {/* Minimalist, clean informational banner */}
                <div className={styles.noRubricBanner}>
                  <div className={styles.noRubricIconWrapper}>
                    <FiInfo size={17} />
                  </div>
                  <div className={styles.noRubricContent}>
                    <div className={styles.noRubricTitle}>
                      No rubric is configured for this question
                    </div>
                    <div className={styles.noRubricSubtitle}>
                      You can assign marks and remarks manually below, or author rubrics in the Quiz Question Editor.
                    </div>
                  </div>
                </div>

                {/* Awarded Marks Input */}
                <div className={styles.manualField}>
                  <label className={styles.manualFieldLabel}>
                    Awarded Marks (out of {currentQuestion?.marks || 0})
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={currentQuestion?.marks || 0}
                    step="0.5"
                    value={manualMarks}
                    onChange={handleManualMarksChange}
                    onKeyDown={(e) => {
                      if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                        e.preventDefault();
                      }
                    }}
                    placeholder={`0 - ${currentQuestion?.marks || 0}`}
                    className={styles.manualInput}
                  />
                </div>

                {/* Remarks / Feedback Input */}
                <div className={styles.manualField}>
                  <label className={styles.manualFieldLabel}>
                    Remarks / Feedback
                  </label>
                  <textarea
                    rows={4}
                    value={manualRemarks}
                    onChange={(e) => setManualRemarks(e.target.value)}
                    placeholder="Add feedback or constructive remarks for the student..."
                    className={styles.manualTextarea}
                  />
                </div>

                {/* Modern Action Buttons */}
                <div className={styles.manualActions}>
                  <button
                    type="button"
                    className={styles.submitBtn}
                    disabled={isSaving}
                    onClick={() => handleSaveManualGrade(false)}
                  >
                    <FiCheck size={16} />
                    <span>{isSaving ? 'Saving...' : 'Save Grade'}</span>
                  </button>

                  <button
                    type="button"
                    className={styles.nextBtn}
                    disabled={isSaving || !nextStudent}
                    onClick={() => handleSaveManualGrade(true)}
                    title="Save and advance to next student (Ctrl+Enter)"
                  >
                    <span>Save & Next</span>
                    <FiArrowRight size={16} />
                  </button>

                  <button
                    type="button"
                    className={styles.resetBtn}
                    disabled={isSaving}
                    onClick={() => {
                      if (currentResponse?.graded) {
                        setManualMarks(currentResponse.scored_marks !== undefined ? String(currentResponse.scored_marks) : '');
                        setManualRemarks(currentResponse.remark || '');
                      } else {
                        setManualMarks('');
                        setManualRemarks('');
                      }
                    }}
                  >
                    <FiRotateCcw size={15} />
                    <span>Reset</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </SplitPane>
      </div>
    </div>
  );
};

export default QuizGradingV2;
